import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Briefcase } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, DetailDrawer, Field, formatDateTime } from "@/components/admin/ui";
import { JOB_STATUS_META, APPOINTMENT_TYPE_LABELS, SOURCE_LABELS } from "@/components/org/office/officeMeta";

export const PersonJobsTab = ({ userId, profile, API }) => {
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    axios.get(`${API}/org/people/${userId}/jobs`, { params, withCredentials: true })
      .then(({ data }) => setJobs(data.jobs))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, userId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Panel title="Jobs" subtitle="Canonical Jobs assigned to this person — the same records shown in Office." testId="person-jobs-tab">
        {error && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="person-jobs-error">{error}</p>}

        <div className="mb-4 flex gap-2" data-testid="person-jobs-filters">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="person-jobs-status-filter"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Statuses</option>
            {profile.job_statuses.map((s) => <option key={s} value={s}>{JOB_STATUS_META[s].label}</option>)}
          </select>
        </div>

        {!jobs ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : jobs.length === 0 ? (
          <EmptyState icon={Briefcase} title="No Jobs yet." message="Jobs assigned to this person from Office or Sales will appear here." testId="person-jobs-empty" />
        ) : (
          <div className="space-y-2" data-testid="person-jobs-list">
            {jobs.map((j) => (
              <button key={j.job_id} type="button" onClick={() => setSelectedJob(j)} data-testid={`person-job-row-${j.job_id}`}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{j.title}{j.customer_name ? ` — ${j.customer_name}` : ""}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{j.team_name} · {APPOINTMENT_TYPE_LABELS[j.appointment_type]} · {formatDateTime(j.scheduled_start)}</p>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold ${JOB_STATUS_META[j.status].cls}`}>{JOB_STATUS_META[j.status].label}</span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      <DetailDrawer open={!!selectedJob} onClose={() => setSelectedJob(null)} title="Job" testId="person-job-detail-drawer">
        {selectedJob && (
          <div className="space-y-4" data-testid="person-job-detail-panel">
            <h3 className="text-lg font-bold text-slate-950 dark:text-white">{selectedJob.title}</h3>
            {selectedJob.customer_name && <p className="text-sm text-slate-500 dark:text-slate-400">{selectedJob.customer_name}</p>}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Team" value={selectedJob.team_name} testId="person-job-detail-team" />
              <Field label="Type" value={APPOINTMENT_TYPE_LABELS[selectedJob.appointment_type]} testId="person-job-detail-type" />
              <Field label="Start" value={formatDateTime(selectedJob.scheduled_start)} testId="person-job-detail-start" />
              <Field label="End" value={formatDateTime(selectedJob.scheduled_end)} testId="person-job-detail-end" />
              <Field label="Status" value={JOB_STATUS_META[selectedJob.status].label} testId="person-job-detail-status" />
              <Field label="Source" value={SOURCE_LABELS[selectedJob.source]} testId="person-job-detail-source" />
            </div>
            {selectedJob.notes && <Field label="Notes" value={selectedJob.notes} testId="person-job-detail-notes" />}
          </div>
        )}
      </DetailDrawer>
    </>
  );
};
